namespace e5 {
    export class Signal<T extends any[] = any> {
        private _enabled: boolean = true;
        private _slots: Slot<T>[] = [];
        private _dispatched: T = null;

        constructor() {
        }

        public get dispatched(): boolean {
            return Boolean(this._dispatched);
        }

        public get enabled(): boolean {
            return this._enabled;
        }

        public set enabled(value: boolean) {
            this._enabled = value;
        }

        public get length(): number {
            return this._slots.length;
        }

        public dispatch(...args: T): void {
            if (!this.enabled) {
                return;
            }

            this._dispatched = args;

            var l: number = this._slots.length;
            var slot: Slot<T>;
            for (var i: number = 0; i < l; ++i) {
                slot = this._slots[i];
                if (!slot.enabled)
                    continue;
                if (slot.once) {
                    this._slots.splice(i, 1);
                    --l;
                    --i;
                }
                slot.execute(this, args);
            }
        }

        public once(listener: (...args: T) => any, context: any = null, namespace: string = null): Slot<T> {
            var sl = this.add(listener, context, namespace);
            sl.once = true;
            return sl;
        }

        public add(listener: (...args: T) => any, context: any = null, namespace: string = null): Slot<T> {
            var sl = new Slot(this, listener, context, namespace);
            this._slots.push(sl);
            return sl;
        }

        public debounce(listener: (...args: T) => any, wait: number = 100, context: any = null, namespace: string = null): SlotDebounce<T> {
            var sl = new SlotDebounce(this, listener, context, namespace);
            sl.wait = wait;
            this._slots.push(sl);
            return sl;
        }

        public throttle(listener: (...args: T) => any, wait: number = 100, context: any = null, namespace: string = null): SlotThrottle<T> {
            var sl = new SlotThrottle(this, listener, context, namespace);
            sl.wait = wait;
            this._slots.push(sl);
            return sl;
        }

        public on(listener: (...args: T) => any, context: any = null, namespace: string = null): Slot<T> {
            var slot = this.add(listener, context, namespace);
            if (this._dispatched) {
                setTimeout(() => {
                    slot.execute.apply(slot, this._dispatched as any);
                }, 1);
            }
            return slot;
        }

        public async promise(useLastValue: boolean = true, maxTime: number = 0): Promise<T> {
            return new Promise<T>((resolve, reject) => {
                if (useLastValue && this._dispatched) {
                    resolve(this._dispatched);
                } else {
                    var timeout: any = null;
                    var slot = this.once((...args: T) => {
                        if (timeout)
                            clearTimeout(timeout);
                        resolve(args);
                    });

                    if (maxTime > 0) {
                        timeout = setTimeout(() => {
                            slot.remove();
                            reject();
                        }, maxTime);
                    }
                }
            });
        }

        public remove(listener: ((...args: T) => any) | string, context: any = null): Slot<T>[] {
            var slots = this.getSlots(listener, context);
            if (slots.length === 0)
                return null;

            for (var slot of slots) {
                var idx: number = this._slots.indexOf(slot);
                this._slots.splice(idx, 1);
                slot.dispose();
            }

            return slots;
        }

        public clear(): void {
            for (var slot of this._slots) {
                slot.dispose();
            }
            this._slots.splice(0, this._slots.length);
        }

        private getSlots(listener: ((...args: T) => any) | string, context: any): Slot<T>[] {
            var slots: Slot<T>[] = [];
            if (typeof listener === "string") {
                for (var slot of this._slots) {
                    if (slot.namespace === listener)
                        slots.push(slot);
                }

            }
            else {
                for (var slot of this._slots) {
                    if (slot.listener === listener && slot.context === context)
                        slots.push(slot);
                }
            }
            return slots;
        }

        public dispose(): void {
            this._dispatched = null;
            this.clear();
            this._slots = null;
        }

        public static create<T extends any[]>(): Signal<T> {
            return new Signal<T>();
        }

        private static _namespace: number = 0;
        public static getUniqueNamespace(): string {
            return "signal-ns-" + this._namespace++;
        }
    }
}